import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ReportApiResponse,
  ReportsApiResponse,
  ReportResponse,
  savedGeneratedReport,
  getPastGeneratedReports,
  updateReportNameById,
  UpdateReportNameResponse,
  getSpecificUserReports,
  getUtilityByZipCode,
  getScheduleByUtility,
  getEnergyBySchedule,
  UtilityByZipCodeResponse,
  ScheduleByUtilityResponse,
  Schedule,
  EnergyByScheduleResponse,
  ApiUsageResponse,
  ApiUsageOverall,
  ApiUsagePerCustomer,
  getApiUsageReports,
  ScheduleDetailTipResponse,
  ScheduleDetailTipData,
  getScheduleDetailTip,
  RewiringAmericaIncentivesResponse,
  getRewiringAmericaIncentives,
  updateSiteReportData,
  UpdateFinancialTableResponse,
} from "./reportAction";

interface ReportState {
  report: ReportResponse | null;
  reports: ReportResponse[];
  loading: boolean;
  error: string | null;
  totalReports: number;
  currentPage: number;
  totalPages: number;
  utilityData: {
    UtilityID: string;
    UtilityName: string;
    State: string;
  } | null;
  utilityLoading: boolean;
  utilityError: string | null;
  scheduleData: Schedule[] | null;
  scheduleLoading: boolean;
  scheduleError: string | null;
  ratekWh: string | null;
  energyLoading: boolean;
  energyError: string | null;
  scheduleDetailData: ScheduleDetailTipData | null;
  scheduleDetailLoading: boolean;
  scheduleDetailError: string | null;
  apiUsageOverall: ApiUsageOverall | null;
  apiUsagePerCustomer: ApiUsagePerCustomer[];
  apiUsageTotal: number;
  apiUsageCurrentPage: number;
  apiUsageTotalPages: number;
  apiUsageLoading: boolean;
  apiUsageError: string | null;
  incentivesData: RewiringAmericaIncentivesResponse["data"] | null;
  incentivesLoading: boolean;
  incentivesError: string | null;
}

const initialState: ReportState = {
  report: null,
  reports: [],
  loading: false,
  error: null,
  totalReports: 0,
  currentPage: 1,
  totalPages: 1,
  utilityData: null,
  utilityLoading: false,
  utilityError: null,
  scheduleData: null,
  scheduleLoading: false,
  scheduleError: null,
  ratekWh: null,
  energyLoading: false,
  energyError: null,
  scheduleDetailData: null,
  scheduleDetailLoading: false,
  scheduleDetailError: null,
  apiUsageOverall: null,
  apiUsagePerCustomer: [],
  apiUsageTotal: 0,
  apiUsageCurrentPage: 1,
  apiUsageTotalPages: 1,
  apiUsageLoading: false,
  apiUsageError: null,
  incentivesData: null,
  incentivesLoading: false,
  incentivesError: null,
};

const reportSlice = createSlice({
  name: "report",
  initialState,
  reducers: {
    resetReportState: (state) => {
      state.report = null;
      state.reports = [];
      state.error = null;
      state.loading = false;
      state.totalReports = 0;
      state.currentPage = 1;
      state.totalPages = 1;
      state.utilityData = null;
      state.utilityLoading = false;
      state.utilityError = null;
      state.scheduleData = null;
      state.scheduleLoading = false;
      state.scheduleError = null;
      state.ratekWh = null;
      state.energyLoading = false;
      state.energyError = null;
      state.scheduleDetailData = null;
      state.scheduleDetailLoading = false;
      state.scheduleDetailError = null;
      state.apiUsageOverall = null;
      state.apiUsagePerCustomer = [];
      state.apiUsageTotal = 0;
      state.apiUsageCurrentPage = 1;
      state.apiUsageTotalPages = 1;
      state.apiUsageLoading = false;
      state.apiUsageError = null;
      state.incentivesData = null;
      state.incentivesLoading = false;
      state.incentivesError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle savedGeneratedReport
      .addCase(savedGeneratedReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        savedGeneratedReport.fulfilled,
        (state, action: PayloadAction<ReportApiResponse>) => {
          state.loading = false;
          state.report = action.payload.data?.report || null;
          state.error = null;
        }
      )
      .addCase(
        savedGeneratedReport.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to submit report";
        }
      )
      // Handle getPastGeneratedReports
      .addCase(getPastGeneratedReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getPastGeneratedReports.fulfilled,
        (state, action: PayloadAction<ReportsApiResponse>) => {
          state.loading = false;
          state.reports = action.payload.data?.reports || [];
          state.totalReports = action.payload.data?.total || 0;
          state.currentPage = action.payload.data?.page || 1;
          state.totalPages = action.payload.data?.pages || 1;
          state.error = null;
        }
      )
      .addCase(
        getPastGeneratedReports.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to retrieve reports";
        }
      )
      // Handle getSpecificUserReports
      .addCase(getSpecificUserReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getSpecificUserReports.fulfilled,
        (state, action: PayloadAction<ReportsApiResponse>) => {
          state.loading = false;
          state.reports = action.payload.data?.reports || [];
          state.totalReports = action.payload.data?.total || 0;
          state.currentPage = action.payload.data?.page || 1;
          state.totalPages = action.payload.data?.pages || 1;
          state.error = null;
        }
      )
      .addCase(
        getSpecificUserReports.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to retrieve reports";
        }
      )
      // Handle updateReportNameById
      .addCase(updateReportNameById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateReportNameById.fulfilled,
        (state, action: PayloadAction<UpdateReportNameResponse>) => {
          state.loading = false;
          const updated = action.payload.data?.report;
          if (updated) {
            const index = state.reports.findIndex((r) => r._id === updated._id);
            if (index !== -1) {
              state.reports[index] = updated;
            }
            if (state.report && state.report._id === updated._id) {
              state.report = updated;
            }
          }
          state.error = null;
        }
      )
      .addCase(
        updateReportNameById.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to update report name";
        }
      )
      // Handle getUtilityByZipCode
      .addCase(getUtilityByZipCode.pending, (state) => {
        state.utilityLoading = true;
        state.utilityError = null;
        state.utilityData = null;
      })
      .addCase(
        getUtilityByZipCode.fulfilled,
        (state, action: PayloadAction<UtilityByZipCodeResponse>) => {
          state.utilityLoading = false;
          state.utilityData = action.payload.data || null;
          state.utilityError = null;
        }
      )
      .addCase(
        getUtilityByZipCode.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.utilityLoading = false;
          state.utilityError =
            action.payload || "Failed to retrieve utility data";
        }
      )
      // Handle getScheduleByUtility
      .addCase(getScheduleByUtility.pending, (state) => {
        state.scheduleLoading = true;
        state.scheduleError = null;
        state.scheduleData = null;
      })
      .addCase(
        getScheduleByUtility.fulfilled,
        (state, action: PayloadAction<ScheduleByUtilityResponse>) => {
          state.scheduleLoading = false;
          state.scheduleData = action.payload.data?.Schedule || null;
          state.scheduleError = null;
        }
      )
      .addCase(
        getScheduleByUtility.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.scheduleLoading = false;
          state.scheduleError =
            action.payload || "Failed to retrieve schedule data";
        }
      )
      // Handle getEnergyBySchedule
      .addCase(getEnergyBySchedule.pending, (state) => {
        state.energyLoading = true;
        state.energyError = null;
        state.ratekWh = null;
      })
      .addCase(
        getEnergyBySchedule.fulfilled,
        (state, action: PayloadAction<EnergyByScheduleResponse>) => {
          state.energyLoading = false;
          state.ratekWh = action.payload.data?.ratekWh || null;
          state.energyError = null;
        }
      )
      .addCase(
        getEnergyBySchedule.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.energyLoading = false;
          state.energyError =
            action.payload || "Failed to retrieve energy rate";
        }
      )
      // Handle getScheduleDetailTip
      .addCase(getScheduleDetailTip.pending, (state) => {
        state.scheduleDetailLoading = true;
        state.scheduleDetailError = null;
        state.scheduleDetailData = null;
      })
      .addCase(
        getScheduleDetailTip.fulfilled,
        (state, action: PayloadAction<ScheduleDetailTipResponse>) => {
          state.scheduleDetailLoading = false;
          state.scheduleDetailData = action.payload.data || null;
          state.scheduleDetailError = null;
        }
      )
      .addCase(
        getScheduleDetailTip.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.scheduleDetailLoading = false;
          state.scheduleDetailError =
            action.payload || "Failed to retrieve schedule detail tip";
        }
      )
      // Handle getApiUsageReports
      .addCase(getApiUsageReports.pending, (state) => {
        state.apiUsageLoading = true;
        state.apiUsageError = null;
        state.apiUsageOverall = null;
        state.apiUsagePerCustomer = [];
        state.apiUsageTotal = 0;
        state.apiUsageCurrentPage = 1;
        state.apiUsageTotalPages = 1;
      })
      .addCase(
        getApiUsageReports.fulfilled,
        (state, action: PayloadAction<ApiUsageResponse>) => {
          state.apiUsageLoading = false;
          state.apiUsageOverall = action.payload.data?.overall || null;
          state.apiUsagePerCustomer = action.payload.data?.perCustomer || [];
          state.apiUsageTotal = action.payload.data?.total || 0;
          state.apiUsageCurrentPage = action.payload.data?.page || 1;
          state.apiUsageTotalPages = action.payload.data?.pages || 1;
          state.apiUsageError = null;
        }
      )
      .addCase(
        getApiUsageReports.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.apiUsageLoading = false;
          state.apiUsageError =
            action.payload || "Failed to retrieve API usage reports";
        }
      )
      // Handle getRewiringAmericaIncentives
      .addCase(getRewiringAmericaIncentives.pending, (state) => {
        state.incentivesLoading = true;
        state.incentivesError = null;
        state.incentivesData = null;
      })
      .addCase(
        getRewiringAmericaIncentives.fulfilled,
        (state, action: PayloadAction<RewiringAmericaIncentivesResponse>) => {
          state.incentivesLoading = false;
          state.incentivesData = action.payload.data || null;
          state.incentivesError = null;
        }
      )
      .addCase(
        getRewiringAmericaIncentives.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.incentivesLoading = false;
          state.incentivesError =
            action.payload || "Failed to retrieve incentives";
        }
      )
      // Handle updateSiteReportData
      .addCase(updateSiteReportData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateSiteReportData.fulfilled,
        (state, action: PayloadAction<UpdateFinancialTableResponse>) => {
          state.loading = false;
          const updated = action.payload.data?.report;
          if (updated) {
            const index = state.reports.findIndex((r) => r._id === updated._id);
            if (index !== -1) {
              state.reports[index] = updated;
            }
            if (state.report && state.report._id === updated._id) {
              state.report = updated;
            }
          }
          state.error = null;
        }
      )
      .addCase(
        updateSiteReportData.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to update financial table";
        }
      );
  },
});

export const { resetReportState } = reportSlice.actions;
export default reportSlice.reducer;
